import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Shrink navbar after scrolling
      setScrolled(currentScrollY > 50);

      // Hide/show based on scroll direction
      if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
        setHidden(true); // Scrolling down
      } else {
        setHidden(false); // Scrolling up
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Leistungen', href: '#leistungen' },
    { label: 'Marketing', href: '#marketing' },
    { label: 'Cases', href: '#cases' },
    { label: 'Über uns', href: '#about' },
    { label: 'Kontakt', href: '#kontakt' },
  ];

  // top koppelt an die Höhe der StatusBar (--statusbar-h). Vorher war der
  // Offset als top-9 hart kodiert — beim Schließen der Leiste blieb dann
  // eine Lücke über der Navigation stehen.
  return (
    <nav
      style={{ top: 'var(--statusbar-h, 0px)' }}
      className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
        hidden ? '-translate-y-full' : 'translate-y-0'
      } ${
        scrolled
          ? 'bg-background/70 backdrop-blur-2xl border-b border-white/10 shadow-lg shadow-black/5'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className={`flex items-center justify-between transition-all duration-500 ${
          scrolled ? 'h-16' : 'h-20'
        }`}>
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className={`inline-flex items-center min-h-11 font-bold tracking-tight cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm transition-all duration-500 ${
              scrolled ? 'text-xl' : 'text-2xl'
            }`}
          >
            <span className="text-foreground">beyer</span>
            <span className="text-primary">digital</span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="nav-link inline-flex items-center min-h-11 text-sm font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm transition-all duration-300 relative group cursor-pointer"
                style={{ transitionDelay: `${index * 50}ms` }}
              >
                {link.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-gold group-hover:w-full transition-all duration-300 ease-out" />
              </a>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden md:block">
            <Button
              variant="default"
              size={scrolled ? 'default' : 'lg'}
              className="magnetic relative overflow-hidden group transition-all duration-500"
              onClick={() => document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' })}
            >
              <span className="relative z-10">Projekt anfragen</span>
              <div className="absolute inset-0 bg-gradient-to-r from-primary via-gold to-primary bg-[length:200%_100%] opacity-0 group-hover:opacity-100 transition-opacity duration-500 animate-gradient-shift" />
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden flex h-11 w-11 items-center justify-center text-foreground hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-6">
              <span className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                mobileMenuOpen ? 'top-3 rotate-45' : 'top-1'
              }`} />
              <span className={`absolute left-0 top-3 w-6 h-0.5 bg-current transition-all duration-300 ${
                mobileMenuOpen ? 'opacity-0 translate-x-2' : 'opacity-100'
              }`} />
              <span className={`absolute left-0 w-6 h-0.5 bg-current transition-all duration-300 ${
                mobileMenuOpen ? 'top-3 -rotate-45' : 'top-5'
              }`} />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden overflow-hidden transition-all duration-500 ${
        mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="bg-background/95 backdrop-blur-2xl border-t border-white/10">
          <div className="container mx-auto px-6 py-6 flex flex-col gap-2">
            {navLinks.map((link, index) => (
              <a
                key={link.href}
                href={link.href}
                className="text-lg font-medium text-foreground hover:text-primary transition-all duration-300 py-3 border-b border-white/5 hover:pl-2"
                style={{
                  transitionDelay: mobileMenuOpen ? `${index * 50}ms` : '0ms',
                  opacity: mobileMenuOpen ? 1 : 0,
                  transform: mobileMenuOpen ? 'translateX(0)' : 'translateX(-20px)'
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                {link.label}
              </a>
            ))}
            <Button
              variant="default"
              size="lg"
              className="w-full mt-4"
              onClick={() => {
                setMobileMenuOpen(false);
                document.querySelector('#kontakt')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Projekt anfragen
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
